import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type { GetWorkersQueryParams, WorkerListItem, WorkersListResponse, RehireWorkerRequest, RehireWorkerResponse, WorkerAuditResponse, TerminateContractRequest, TerminateContractResponse } from "../types";
import type { WorkerDetailResponse } from "../pages/WorkerDetailPage/types";

export const WORKER_AUDIT_PAGE_SIZE = 20;

export const workersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWorkers: builder.query<WorkersListResponse, GetWorkersQueryParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();

        if (params?.status) searchParams.append("status", params.status);
        if (params?.search) searchParams.append("search", params.search);

        return {
          url: `/api/hr/workers/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
        };
      },
      transformResponse(response: WorkersListResponse | WorkerListItem[]) {
        if (Array.isArray(response)) {
          return { workers: response, import_drafts: [] };
        }
        return response;
      },
      providesTags: [{ type: "NGOWorkers" as const, id: "LIST" }],
    }),

    downloadWorkerProfilePreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/hr/workers/${id}/preview-profile/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),

    rehireWorker: builder.mutation<RehireWorkerResponse, RehireWorkerRequest>({
      query: (body) => ({
        url: "/api/hr/workers/rehire/",
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "NGOWorkers" as const, id: "LIST" }],
    }),

    getWorkerDetail: builder.query<WorkerDetailResponse, number>({
      query: (id) => ({
        url: `/api/hr/workers/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "WorkerDetail" as const, id }],
    }),

    updateWorkerDepartment: builder.mutation<unknown, { id: number; department_id: number | null }>({
      query: ({ id, department_id }) => ({
        url: `/api/hr/workers/${id}/`,
        method: "PATCH",
        body: { department_id },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "WorkerDetail" as const, id },
        { type: "NGOWorkers" as const, id: "LIST" },
        { type: "Departments" as const, id: "LIST" },
      ],
    }),

    terminateContract: builder.mutation<TerminateContractResponse, TerminateContractRequest>({
      query: ({ contractId, date, reason, deactivate_worker }) => ({
        url: `/api/hr/contracts/${contractId}/terminate/`,
        method: "POST",
        body: { date, reason, deactivate_worker },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { workerId }) => [
        { type: "WorkerDetail" as const, id: workerId },
        { type: "NGOWorkers" as const, id: "LIST" },
        { type: "Contracts" as const, id: "LIST" },
        "Timesheets",
        "PendingActions",
      ],
    }),

    getWorkerAudit: builder.query<WorkerAuditResponse, { workerId: number; page: number }>({
      query: ({ workerId, page }) => ({
        url: `/api/hr/workers/${workerId}/audit/`,
        method: "GET",
        params: { page, page_size: WORKER_AUDIT_PAGE_SIZE },
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, { workerId }) => [{ type: "WorkerDetail" as const, id: workerId }],
    }),
  }),
});

export const {
  useGetWorkersQuery,
  useDownloadWorkerProfilePreviewMutation,
  useRehireWorkerMutation,
  useGetWorkerDetailQuery,
  useUpdateWorkerDepartmentMutation,
  useGetWorkerAuditQuery,
  useTerminateContractMutation,
} = workersApi;

